import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useRouter, useRouterState } from "@tanstack/react-router";
import { useAuth } from "./AuthContext";
import type { Scope, ScopeTree } from "../types";
import { normalizeScopeForTree, resolveScope } from "./scopeResolution";
import {
  isValidScope,
  pickInitialScope,
  readRecentScopes,
  readScopeFromUrl,
  RECENT_SCOPES_LIMIT,
  scopesEqual,
  writeLegacyStorageKeys,
  writeRecentScopes,
  writeScopeToStorage,
  writeScopeToUrl,
} from "./scopeStorage";

export { normalizeScopeForTree, resolveScope } from "./scopeResolution";

export interface ScopeContextValue {
  scope: Scope;
  setScope: (next: Scope) => void;
  chatbotIds: string[];
  label: string[];
  tree: ScopeTree;
  recent: Scope[];
}

const ScopeContext = createContext<ScopeContextValue | null>(null);
const EMPTY_SCOPE_TREE: ScopeTree = Object.freeze({ organizations: [] });

export function ScopeProvider({ children }: { children?: ReactNode }) {
  const auth = useAuth();
  const tree = auth.userSettings?.scope_tree ?? EMPTY_SCOPE_TREE;
  const location = useLocation();
  const router = useRouter();
  const routerStatus = useRouterState({ select: (state) => state.status });
  const [scope, setScopeState] = useState<Scope>(() => pickInitialScope());
  const [recent, setRecent] = useState<Scope[]>(() => readRecentScopes());
  const resolvedForSync = useMemo(() => resolveScope(tree, scope), [tree, scope]);

  useEffect(() => {
    if (
      location.pathname !== "/login" &&
      location.pathname !== "/auth/callback" &&
      routerStatus === "idle"
    ) {
      // Read the raw query from history, not from `location.searchStr`: the
      // router state's string is re-serialized through the search codec and
      // mangles repeated keys other pages rely on (#978). `location` stays a
      // dependency so the write re-runs on every navigation.
      writeScopeToUrl((href) => router.history.replace(href), router.history.location, scope);
    }
  }, [scope, location, router, routerStatus]);

  if (typeof window !== "undefined" && auth.isAuthenticated) {
    writeLegacyStorageKeys(scope, resolvedForSync.chatbotIds);
  }

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handlePopState = () => {
      const fromUrl = readScopeFromUrl();
      if (fromUrl && !scopesEqual(fromUrl, scope)) setScopeState(fromUrl);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [scope]);

  const setScope = useCallback(
    (next: Scope) => {
      if (!isValidScope(next)) return;
      const normalizedNext = normalizeScopeForTree(tree, next);
      setScopeState((previous) => {
        if (scopesEqual(previous, normalizedNext)) return previous;
        writeScopeToStorage(normalizedNext);
        writeLegacyStorageKeys(normalizedNext, resolveScope(tree, normalizedNext).chatbotIds);
        setRecent((previousRecent) => {
          const filtered = previousRecent.filter((entry) => !scopesEqual(entry, normalizedNext));
          const updated = [normalizedNext, ...filtered].slice(0, RECENT_SCOPES_LIMIT);
          writeRecentScopes(updated);
          return updated;
        });
        return normalizedNext;
      });
    },
    [tree],
  );

  const value = useMemo<ScopeContextValue>(() => {
    const resolved = resolveScope(tree, scope);
    return {
      scope,
      setScope,
      chatbotIds: resolved.chatbotIds,
      label: resolved.label,
      tree,
      recent,
    };
  }, [scope, tree, setScope, recent]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeContextValue {
  return (
    useContext(ScopeContext) ?? {
      scope: { type: "all" },
      setScope: () => {},
      chatbotIds: [],
      label: [],
      tree: EMPTY_SCOPE_TREE,
      recent: [],
    }
  );
}
